import { useState } from 'react';
import { BarChart3 } from 'lucide-react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { DashboardCard } from '@/ui/charts/DashboardCard';
import { BarChartCard } from '@/ui/charts/BarChartCard';
import { ErrorBoundary } from '@/ui/ErrorBoundary';
import { ErrorState, type ErrorStateVariant } from '@/ui/ErrorState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { ToggleGroup, ToggleGroupItem } from '@/ui/base/toggle-group';

import { AmostraSuspensa } from '../AmostraSuspensa';
import { ComponentShowcase } from '../ComponentShowcase';
import { MEDIDAS } from '../medidas';

const POR_DRE = [
  { name: 'Butantã', total: 84 },
  { name: 'Campo Limpo', total: 121 },
  { name: 'Ipiranga', total: 97 },
  { name: 'Penha', total: 66 },
];

const VARIANTES: { valor: ErrorStateVariant; rotulo: string }[] = [
  { valor: 'load-failed', rotulo: 'Falha ao carregar' },
  { valor: 'forbidden', rotulo: 'Sem acesso (403)' },
  { valor: 'session-expired', rotulo: 'Sessão expirada (401)' },
  { valor: 'not-found', rotulo: 'Página não encontrada' },
  { valor: 'resource-not-found', rotulo: 'Registro não encontrado' },
  { valor: 'server-error', rotulo: 'Erro inesperado' },
  { valor: 'timeout', rotulo: 'Demorando demais' },
];

type EstadoSimulado = 'carregando' | 'sessao' | 'recusa' | 'falha' | 'pronto';

/** Os erros que o cliente da API devolveria, montados aqui com a classe de verdade. */
const ERRO_DO_ESTADO: Record<EstadoSimulado, unknown> = {
  carregando: null,
  sessao: new ErroApi(401, 'Sessão expirada'),
  recusa: new ErroApi(403, 'Sem acesso'),
  falha: new ErroApi(503, 'Indisponível'),
  pronto: null,
};

/** Lança ao renderizar: é o que o ErrorBoundary existe para pegar. */
function ComponenteQueQuebra(): never {
  throw new Error('Falha provocada pela amostra da documentação');
}

/** Gráficos e o retorno ao usuário: avisos, falhas e os estados de quem busca dados. */
export function AmostrasDeRetorno() {
  const [variante, definirVariante] = useState<ErrorStateVariant>('load-failed');
  const [estado, definirEstado] = useState<EstadoSimulado>('falha');
  const [quebrado, definirQuebrado] = useState(false);
  const [geracao, definirGeracao] = useState(0);

  return (
    <>
      <ComponentShowcase
        id="pie-chart-card"
        title="PieChartCard"
        description="Gráfico de rosca num DashboardCard, com as séries --chart-N e o rótulo de cada fatia na cor do texto. Sem dados, não renderiza: a tela mostra o estado vazio."
        code={`<PieChartCard title="Ordens de Serviço por tipo" data={[{ name: 'Comum', value: 612 }, …]} />`}
        propsTable={[
          ['title', 'string', '-', 'Título do cartão'],
          ['data', '{ name: string; value: number }[]', '-', 'Fatias; vazio = nada renderiza'],
          ['icon', 'ComponentType', 'BarChart3', 'Ícone do cartão'],
          ['height', 'number', '240', 'Altura do gráfico em px'],
          ['onSliceClick', '(name, value) => void', '-', 'Clique numa fatia (ex.: filtrar)'],
          ['className', 'string', '-', 'Classes do cartão'],
        ]}
      >
        <AmostraSuspensa item="pie-chart-card" />
      </ComponentShowcase>

      <ComponentShowcase
        id="bar-chart-card"
        title="BarChartCard"
        description="Gráfico de barras num DashboardCard, com o degradê do tema e a dica no fundo do popover. Formatadores próprios para a dica e o eixo (ex.: dinheiro)."
        code={`<BarChartCard
  title="Inscrições por DRE"
  data={[{ name: 'Butantã', total: 84 }, …]}
  tooltipFormatter={(v) => \`\${v} inscrições\`}
/>`}
        propsTable={[
          ['title', 'string', '-', 'Título do cartão'],
          ['data', '{ name: string; total: number }[]', '-', 'Barras'],
          ['icon', 'ComponentType', '-', 'Ícone do cartão'],
          ['height', 'number', '-', 'Altura do gráfico em px'],
          ['tooltipFormatter', '(value: number) => string', '-', 'Texto da dica'],
          ['yAxisFormatter', '(value: number) => string', '-', 'Texto do eixo'],
          ['onBarClick', '(name, total) => void', '-', 'Clique numa barra'],
          ['className', 'string', '-', 'Classes do cartão'],
        ]}
      >
        <div className="w-full max-w-md">
          <BarChartCard
            title="Inscrições por DRE"
            icon={BarChart3}
            data={POR_DRE}
            tooltipFormatter={(v) => `${v} inscrições`}
          />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="dashboard-card"
        title="DashboardCard"
        description="Cartão dos painéis: ícone em laranja, título e subtítulo, e o conteúdo livre (gráfico, lista, número). É a moldura dos dois gráficos acima."
        code={`<DashboardCard title="Pendências da semana" subtitle="Por responsável" icon={ClipboardList}>
  {conteudo}
</DashboardCard>`}
        propsTable={[
          ['title', 'string', '-', 'Título do cartão'],
          ['subtitle', 'string', '-', 'Subtítulo'],
          ['icon', 'ComponentType', '-', 'Ícone, no fundo laranja'],
          ['iconClassName', 'string', '-', 'Classes do ícone'],
          ['children', 'ReactNode', '-', 'Conteúdo'],
          ['className', 'string', '-', 'Classes do cartão'],
        ]}
      >
        <div className="w-full max-w-md">
          <DashboardCard title="Pendências da semana" subtitle="Por responsável" icon={BarChart3}>
            <p className="text-sm text-muted-foreground">Conteúdo livre do cartão.</p>
          </DashboardCard>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="notificacao"
        title="Notificacao"
        description="O aviso de sucesso ou falha de uma ação da página (salvar, decidir, importar), fixo junto da ação até a pessoa fechar ou a página substituir — quem audita uma captura precisa achar o resultado onde a ação foi feita. Não é toast. A tela guarda um Aviso no estado e mostra a Notificacao quando ele existe."
        code={`
const [aviso, setAviso] = useState<Aviso | null>(null);
// depois da ação: setAviso({ tom: 'sucesso', mensagem: 'Apontamento enviado para análise.' });

{aviso && <Notificacao {...aviso} aoFechar={() => setAviso(null)} />}`}
        propsTable={[
          ['tom', '"sucesso" | "falha"', '-', 'Resultado da ação'],
          ['mensagem', 'string', '-', 'Frase curta: o que aconteceu ou o que fazer'],
          ['aoFechar', '() => void', '-', 'Mostra o botão de fechar'],
        ]}
      >
        <AmostraSuspensa
          item="notificacao"
          medida={`sucesso ${MEDIDAS.notificacaoDeSucesso}; falha ${MEDIDAS.notificacaoDeFalha}`}
        />
      </ComponentShowcase>

      <ComponentShowcase
        id="error-state"
        title="ErrorState"
        description="Estado de erro, acesso ou registro ausente, num componente só: sete variantes com ícone, título e texto próprios, e até duas ações. Dentro de um cartão por padrão; fullPage ocupa a tela. Nunca mostra erro técnico (o detalhe vai para o log)."
        code={`<ErrorState
  variant="load-failed"
  primaryAction={{ label: 'Tentar de novo', onClick: () => refetch() }}
/>`}
        propsTable={[
          [
            'variant',
            'ErrorStateVariant',
            '-',
            'forbidden, not-found, session-expired, load-failed, resource-not-found, server-error ou timeout',
          ],
          ['title', 'string', 'pela variante', 'Título próprio'],
          ['description', 'string', 'pela variante', 'Texto próprio'],
          ['primaryAction', '{ label, to?, onClick?, variant? }', '-', 'Ação principal'],
          ['secondaryAction', '{ label, to?, onClick?, variant? }', '-', 'Ação secundária'],
          ['fullPage', 'boolean', 'false', 'Ocupa a tela inteira'],
          ['technicalDetails', 'string', '-', 'Só para o ErrorBoundary; tela comum não usa'],
          ['children', 'ReactNode', '-', 'Conteúdo a mais, abaixo do texto'],
          ['className', 'string', '-', 'Classes do contêiner'],
        ]}
      >
        <div className="w-full space-y-3">
          <div className="flex max-w-xs flex-col gap-1.5">
            <Label htmlFor="ds-variante-do-erro">Variante</Label>
            <Select value={variante} onValueChange={(v) => definirVariante(v as ErrorStateVariant)}>
              <SelectTrigger id="ds-variante-do-erro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VARIANTES.map((v) => (
                  <SelectItem key={v.valor} value={v.valor}>
                    {v.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ErrorState
            variant={variante}
            primaryAction={{ label: 'Voltar ao início', to: '/admin/design-system' }}
          />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="estado-da-tela"
        title="EstadoDaTela"
        description="Três dos quatro estados de quem busca dados, num lugar só: carregando (esqueleto com a forma do que vem), sessão vencida (401: entrar de novo), sem acesso (403: sem “Tentar de novo”, porque insistir não resolve) e falha (“Tentar de novo”). O vazio é da própria tela, dentro de children. Os erros abaixo são ErroApi de verdade."
        code={`<EstadoDaTela
  carregando={ordens.isPending}
  erro={ordens.error}
  aoTentarDeNovo={() => void ordens.refetch()}
  descricao="as ordens de serviço"
  esqueleto={{ colunas: colunas.length }}
>
  {linhas.length === 0 ? <EmptyState … /> : <DataTable … />}
</EstadoDaTela>`}
        propsTable={[
          ['carregando', 'boolean', '-', 'Primeira busca em andamento'],
          ['erro', 'unknown', '-', 'Falha da busca (ErroApi); nulo = deu certo'],
          ['aoTentarDeNovo', '() => void', '-', 'Ação de "Tentar de novo"'],
          ['descricao', 'string', '-', 'O que carrega: "as inscrições"'],
          ['esqueleto', 'EsqueletoDaTela', '"cartao"', 'cartao, lista-de-cartoes ou { colunas }'],
          [
            'recusa',
            'RecusaDaTela',
            '"sem-acesso"',
            '403: sem-acesso, tentar-de-novo ou { titulo?, descricao?, acao? }',
          ],
          ['children', 'ReactNode', '-', 'O conteúdo, quando deu certo (inclui o vazio)'],
        ]}
      >
        <div className="w-full space-y-3">
          <ToggleGroup
            type="single"
            value={estado}
            onValueChange={(v) => v && definirEstado(v as EstadoSimulado)}
            aria-label="Estado da busca de exemplo"
            className="flex-wrap justify-start"
          >
            <ToggleGroupItem value="carregando">Carregando</ToggleGroupItem>
            <ToggleGroupItem value="sessao">401</ToggleGroupItem>
            <ToggleGroupItem value="recusa">403</ToggleGroupItem>
            <ToggleGroupItem value="falha">Falha</ToggleGroupItem>
            <ToggleGroupItem value="pronto">Deu certo</ToggleGroupItem>
          </ToggleGroup>
          <EstadoDaTela
            carregando={estado === 'carregando'}
            erro={ERRO_DO_ESTADO[estado]}
            aoTentarDeNovo={() => definirEstado('pronto')}
            descricao="as ordens de serviço"
            esqueleto={{ colunas: 3 }}
          >
            <p className="rounded-xl border border-border bg-card p-4 text-sm text-foreground">
              O conteúdo da tela aparece aqui quando a busca dá certo.
            </p>
          </EstadoDaTela>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="error-boundary"
        title="ErrorBoundary"
        description="Rede de segurança do App: quando um componente quebra ao renderizar, troca a tela pelo ErrorState de erro inesperado, em tela cheia, com recarregar e limpar o cache (versão nova publicada). O botão abaixo quebra um componente de verdade dentro de um ErrorBoundary."
        code={`<ErrorBoundary>
  <App />
</ErrorBoundary>`}
        propsTable={[['children', 'ReactNode', '-', 'O que fica protegido']]}
      >
        <div className="w-full space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => definirQuebrado(true)}>
              Quebrar um componente
            </Button>
            {quebrado && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  definirQuebrado(false);
                  definirGeracao((g) => g + 1);
                }}
              >
                Restaurar a amostra
              </Button>
            )}
          </div>
          <ErrorBoundary key={geracao}>
            {quebrado ? (
              <ComponenteQueQuebra />
            ) : (
              <p className="text-sm text-muted-foreground">Componente funcionando.</p>
            )}
          </ErrorBoundary>
        </div>
      </ComponentShowcase>
    </>
  );
}
